import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import firebaseService from '../services/firebaseService';
import './Posts.css';

const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [commentedTickets, setCommentedTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const setupSubscription = async () => {
      try {
        // Set up real-time listener for posts
        unsubscribe = await firebaseService.subscribeToPosts((updatedPosts) => {
          if (!isMounted) return;
          console.log('Posts - Received posts:', updatedPosts);
          
          // Sort by date (newest first) and filter only published
          const publishedPosts = updatedPosts
            .filter(post => post.published)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          
          setPosts(publishedPosts);
          setLoading(false);
        });
      } catch (error) {
        console.error('Error setting up posts subscription:', error);
        if (isMounted) setLoading(false);
      }
    };

    const loadInitialData = async () => {
      try {
        const allPosts = await firebaseService.getAllPosts();
        if (!isMounted) return;
        
        const publishedPosts = allPosts
          .filter(post => post.published)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        
        setPosts(publishedPosts);
        setLoading(false);
      } catch (error) {
        console.error('Error loading posts:', error);
        if (isMounted) setLoading(false);
      }
    };

    loadInitialData();
    setupSubscription();

    // Load matches with comments
    const loadCommentedMatches = async () => {
      try {
        const matches = await firebaseService.getAllMatches();
        const withComments = matches.filter(m => m.comment && m.comment.trim() !== '');
        withComments.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        setCommentedTickets(withComments);
      } catch (e) {
        console.error('Error loading commented matches:', e);
      }
    };

    const unsubscribeMatches = firebaseService.subscribeToMatches
      ? (() => {
          let unsub = null;
          firebaseService.subscribeToMatches((updatedMatches) => {
            const withComments = updatedMatches.filter(m => m.comment && m.comment.trim() !== '');
            withComments.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
            setCommentedTickets(withComments);
          }).then(fn => { unsub = fn; });
          return () => { if (unsub) unsub(); };
        })()
      : null;

    loadCommentedMatches();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
      if (unsubscribeMatches) unsubscribeMatches();
    };
  }, []);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm');
    } catch {
      return dateString;
    }
  };

  const commentDateNames = {
    months: ['იანვარი', 'თებერვალი', 'მარტი', 'აპრილი', 'მაისი', 'ივნისი', 'ივლისი', 'აგვისტო', 'სექტემბერი', 'ოქტომბერი', 'ნოემბერი', 'დეკემბერი'],
    weekdays: ['ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ', 'კვი'],
    fullWeekdays: ['ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი', 'კვირა']
  };

  const currentDate = new Date();
  const currentDateLabel = `${currentDate.getDate()} ${commentDateNames.months[currentDate.getMonth()]}, ${commentDateNames.fullWeekdays[(currentDate.getDay() + 6) % 7]}, ${currentDate.getFullYear()}`;

  const getCommentDateKey = (dateValue) => {
    if (!dateValue) return 'unknown';

    const rawValue = String(dateValue);
    const dateOnlyMatch = rawValue.match(/^\d{4}-\d{2}-\d{2}/);
    if (dateOnlyMatch) return dateOnlyMatch[0];

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return 'unknown';

    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatCommentGroupDate = (dateKey) => {
    if (dateKey === 'unknown') return 'უცნობი თარიღი';

    const date = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(date.getTime())) return 'უცნობი თარიღი';

    const weekday = commentDateNames.fullWeekdays[(date.getDay() + 6) % 7];
    const month = commentDateNames.months[date.getMonth()];
    return `${date.getDate()} ${month}, ${weekday}, ${date.getFullYear()}`;
  };

  const commentGroups = Object.entries(commentedTickets.reduce((groups, ticket) => {
    const dateKey = getCommentDateKey(ticket.date);
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(ticket);
    return groups;
  }, {})).sort(([dateA], [dateB]) => {
    if (dateA === 'unknown') return 1;
    if (dateB === 'unknown') return -1;
    return new Date(`${dateB}T12:00:00`) - new Date(`${dateA}T12:00:00`);
  });

  if (loading) {
    return (
      <div className="posts-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>იტვირთება...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="posts-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="posts-page-header">
        <h1 className="posts-page-title">დღის კომენტარი</h1>
        <p className="posts-page-date">{currentDateLabel}</p>
      </div>

      {/* Ticket Comments Section */}
      {commentedTickets.length > 0 && (
        <div className="ticket-comments-section">
          <div className="comment-date-groups">
            {commentGroups.map(([dateKey, dateTickets]) => (
              <section key={dateKey} className="comment-date-group">
                <h2 className="comment-date-title">{formatCommentGroupDate(dateKey)}</h2>
                <div className="comment-date-list">
                  {dateTickets.map((ticket, index) => (
                    <motion.div
                      key={ticket.id}
                      className="ticket-comment-card"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.07 }}
                    >
                      <div className="tc-comment-text">
                        {ticket.comment.split(/\n/).map((line, i, arr) => (
                          <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}

      {posts.length === 0 ? (
        <div className="no-posts">
          <p>📝 ჯერ არ არის გამოქვეყნებული სიახლეები</p>
        </div>
      ) : (
        <div className="posts-grid">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              className="post-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => setSelectedPost(post)}
            >
              {post.image && (
                <div className="post-image">
                  <img src={post.image} alt={post.title} />
                </div>
              )}
              <div className="post-content">
                <div className="post-meta">
                  <span className="post-date">{formatDate(post.createdAt)}</span>
                  {post.category && (
                    <span className="post-category">{post.category}</span>
                  )}
                </div>
                <h2 className="post-title">{post.title}</h2>
                <div 
                  className="post-excerpt"
                  dangerouslySetInnerHTML={{ 
                    __html: post.content.substring(0, 200) + '...' 
                  }}
                />
                <button className="read-more-btn">
                  სრულად წაკითხვა →
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      )}

      {/* Modal for full post view */}
      {selectedPost && (
        <motion.div 
          className="post-modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setSelectedPost(null)}
        >
          <motion.div 
            className="post-modal"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="close-modal-btn"
              onClick={() => setSelectedPost(null)}
            >
              ✕
            </button>
            
            {selectedPost.image && (
              <div className="modal-post-image">
                <img src={selectedPost.image} alt={selectedPost.title} />
              </div>
            )}
            
            <div className="modal-post-content">
              <div className="modal-post-meta">
                <span className="post-date">{formatDate(selectedPost.createdAt)}</span>
                {selectedPost.category && (
                  <span className="post-category">{selectedPost.category}</span>
                )}
              </div>
              
              <h1 className="modal-post-title">{selectedPost.title}</h1>
              
              <div 
                className="modal-post-body"
                dangerouslySetInnerHTML={{ __html: selectedPost.content }}
              />
              
              {selectedPost.author && (
                <div className="post-author">
                  <p>ავტორი: {selectedPost.author}</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default Posts;
